import React, { useEffect, useState } from 'react';
import { Star, Send, Quote, MessageSquarePlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../components/ui/dialog';
import { toast } from 'sonner';

const PROGRAMS = ['STARTER', 'MOMENTUM', 'IMMERSION', 'FLUENCY JOURNEY', 'ELITE', 'DELE Prep', 'School Support', 'Other'];

const StarRating = ({ value, onChange, size = 'lg', readOnly = false }) => {
  const [hover, setHover] = useState(0);
  const cls = size === 'lg' ? 'h-7 w-7' : 'h-4 w-4';
  return (
    <div className="flex gap-1" role={readOnly ? undefined : 'radiogroup'} aria-label="Rating">
      {[1, 2, 3, 4, 5].map((i) => {
        const active = (hover || value) >= i;
        return (
          <button
            key={i}
            type="button"
            disabled={readOnly}
            data-testid={readOnly ? undefined : `star-${i}`}
            aria-label={`${i} star${i > 1 ? 's' : ''}`}
            aria-checked={value === i}
            role={readOnly ? undefined : 'radio'}
            onMouseEnter={() => !readOnly && setHover(i)}
            onMouseLeave={() => !readOnly && setHover(0)}
            onClick={() => !readOnly && onChange?.(i)}
            className={`transition-transform duration-200 ${readOnly ? 'cursor-default' : 'hover:scale-110 cursor-pointer'}`}
          >
            <Star
              className={`${cls} ${active ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} transition-colors`}
            />
          </button>
        );
      })}
    </div>
  );
};

const cardThemes = [
  { bg: 'from-pink-50 to-white', border: 'border-pink-300', accent: 'text-pink-600' },
  { bg: 'from-purple-50 to-white', border: 'border-purple-300', accent: 'text-purple-600' },
  { bg: 'from-blue-50 to-white', border: 'border-blue-300', accent: 'text-blue-600' },
  { bg: 'from-yellow-50 to-white', border: 'border-yellow-300', accent: 'text-yellow-700' },
  { bg: 'from-green-50 to-white', border: 'border-green-300', accent: 'text-green-700' }
];

const Testimonials = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    rating: 0,
    comment: '',
    program: '',
    location: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/testimonials?limit=30`);
      if (!r.ok) throw new Error('fetch');
      const data = await r.json();
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Prefill + auto-open from URL params (?leave_review=1&name=...&program=...)
    try {
      const sp = new URLSearchParams(window.location.search);
      const wantsToReview = sp.get('leave_review') === '1';
      const name = sp.get('name') || '';
      const program = sp.get('program') || '';
      if (wantsToReview || name || program) {
        setForm((p) => ({ ...p, name, program }));
        if (wantsToReview) {
          // Slight delay so the section is mounted/scrollable
          setTimeout(() => {
            setOpen(true);
            document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth' });
          }, 600);
        }
        // Clean the URL so it doesn't reopen on refresh
        const url = new URL(window.location.href);
        ['leave_review', 'name', 'program'].forEach((k) => url.searchParams.delete(k));
        window.history.replaceState({}, '', url.pathname + (url.search ? '?' + url.searchParams.toString() : '') + url.hash);
      }
    } catch {
      // no-op
    }
    // eslint-disable-next-line
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.rating < 1) {
      toast.error('Please select a star rating');
      return;
    }
    if (form.comment.trim().length < 10) {
      toast.error('Comment must be at least 10 characters');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/testimonials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail?.[0]?.msg || 'Could not submit');
      }
      toast.success('Thank you for your review!', {
        description: 'Your testimonial is now live on the page.'
      });
      setForm({ name: '', email: '', rating: 0, comment: '', program: '', location: '' });
      setOpen(false);
      await load();
    } catch (err) {
      toast.error('Could not submit your review', {
        description: 'Please check the fields and try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const avgRating =
    items.length > 0
      ? (items.reduce((s, t) => s + t.rating, 0) / items.length).toFixed(1)
      : null;

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="py-20 bg-gradient-to-br from-pink-50 via-purple-50 to-yellow-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full border-2 border-purple-200 mb-4">
            <Quote className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-bold text-purple-700">Student Stories</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-purple-600 mb-3">
            What Students Say
          </h2>
          {avgRating && (
            <div className="flex items-center justify-center gap-3 mt-3">
              <StarRating value={Math.round(parseFloat(avgRating))} readOnly />
              <span className="text-base md:text-lg font-bold text-purple-700">
                {avgRating} <span className="text-sm font-normal text-purple-500">· {items.length} review{items.length === 1 ? '' : 's'}</span>
              </span>
            </div>
          )}
        </div>

        {/* Cards */}
        {loading ? (
          <div className="text-center text-purple-500 py-10">Loading reviews…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-purple-600 py-10 bg-white/60 rounded-3xl border-3 border-purple-200 max-w-xl mx-auto">
            <p className="font-bold mb-1">Be the first to share your experience!</p>
            <p className="text-sm text-purple-500">Tap the button below to leave a review.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((t, i) => {
              const theme = cardThemes[i % cardThemes.length];
              return (
                <div
                  key={t.id}
                  data-testid={`testimonial-card-${i}`}
                  className={`relative bg-gradient-to-br ${theme.bg} border-3 ${theme.border} rounded-3xl p-6 shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 flex flex-col`}
                >
                  <Quote className={`absolute top-5 right-5 h-8 w-8 ${theme.accent} opacity-20`} />
                  <StarRating value={t.rating} readOnly size="sm" />
                  <p className="mt-4 text-sm text-gray-700 leading-relaxed flex-1">
                    "{t.comment}"
                  </p>
                  <div className={`mt-5 pt-4 border-t-2 ${theme.border}`}>
                    <p className={`font-bold ${theme.accent}`}>{t.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {[t.program, t.location].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CTA Bar */}
        <div className="mt-12 text-center">
          <Button
            data-testid="open-testimonial-form"
            onClick={() => setOpen(true)}
            className="bg-gradient-to-r from-pink-300 via-purple-300 to-blue-300 hover:from-pink-400 hover:to-blue-400 text-purple-800 font-bold px-8 py-6 text-base md:text-lg rounded-full border-3 border-purple-300 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            <MessageSquarePlus className="mr-2 h-5 w-5" />
            Share Your Experience
          </Button>
          <p className="text-xs text-purple-500 mt-3">Your review will be published instantly on this page.</p>
        </div>
      </div>

      {/* Review Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg border-4 border-purple-300 rounded-3xl bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-purple-700">
              Share Your Experience
            </DialogTitle>
            <DialogDescription className="text-purple-600">
              Tell other students how Hola Spanglish helped you. Your review will appear right away.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="testimonial-form">
            <div className="space-y-2">
              <Label className="text-purple-700 font-bold">Your rating *</Label>
              <StarRating
                value={form.rating}
                onChange={(v) => setForm((p) => ({ ...p, rating: v }))}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="t-name" className="text-purple-700 font-bold text-sm">Name *</Label>
                <Input
                  id="t-name"
                  name="name"
                  data-testid="testimonial-name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Sarah M."
                  required
                  minLength={2}
                  maxLength={80}
                  className="border-2 border-purple-300 focus:border-pink-400 rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="t-email" className="text-purple-700 font-bold text-sm">Email *</Label>
                <Input
                  id="t-email"
                  name="email"
                  type="email"
                  data-testid="testimonial-email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@email.com"
                  required
                  className="border-2 border-purple-300 focus:border-pink-400 rounded-xl"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="t-program" className="text-purple-700 font-bold text-sm">Program taken</Label>
                <select
                  id="t-program"
                  name="program"
                  data-testid="testimonial-program"
                  value={form.program}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border-2 border-purple-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-pink-400 bg-white text-gray-700 text-sm"
                >
                  <option value="">Select…</option>
                  {PROGRAMS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="t-location" className="text-purple-700 font-bold text-sm">City / Country</Label>
                <Input
                  id="t-location"
                  name="location"
                  data-testid="testimonial-location"
                  value={form.location}
                  onChange={handleChange}
                  placeholder="Brooklyn, NY"
                  maxLength={80}
                  className="border-2 border-purple-300 focus:border-pink-400 rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="t-comment" className="text-purple-700 font-bold text-sm">Your experience *</Label>
              <Textarea
                id="t-comment"
                name="comment"
                data-testid="testimonial-comment"
                value={form.comment}
                onChange={handleChange}
                rows={4}
                placeholder="What did you love about your sessions with Hola Spanglish?"
                required
                minLength={10}
                maxLength={600}
                className="border-2 border-purple-300 focus:border-pink-400 resize-none rounded-xl"
              />
              <p className="text-xs text-purple-500 text-right">{form.comment.length}/600</p>
            </div>

            <DialogFooter className="mt-2">
              <Button
                type="submit"
                data-testid="submit-testimonial"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 hover:from-pink-500 hover:to-blue-500 text-white font-bold py-5 text-base rounded-full border-3 border-purple-300 transition-all duration-300 hover:scale-[1.02]"
              >
                {submitting ? 'Submitting…' : (<><Send className="mr-2 h-4 w-4" />Publish my review</>)}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Testimonials;
